import { useState, useEffect, useRef } from "react";
import { api } from "../lib/api";

/**
 * Renders an image that requires authentication.
 * If `src` starts with http it's treated as a public URL (S3) and used directly.
 * Otherwise it fetches via the authenticated API client and renders as a blob URL.
 */
export default function AuthImage({ src, alt, className, onError }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const prevBlobRef = useRef(null);

  const isPublic = src && (src.startsWith("http://") || src.startsWith("https://"));

  useEffect(() => {
    if (isPublic || !src) {
      setBlobUrl(null);
      return;
    }

    let cancelled = false;

    api.get(src, { responseType: "blob" })
      .then(({ data }) => {
        if (cancelled) return;
        const url = URL.createObjectURL(data);
        // Revoke previous blob URL
        if (prevBlobRef.current) URL.revokeObjectURL(prevBlobRef.current);
        prevBlobRef.current = url;
        setBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setBlobUrl(null);
      });

    return () => {
      cancelled = true;
    };
  }, [src, isPublic]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (prevBlobRef.current) URL.revokeObjectURL(prevBlobRef.current);
    };
  }, []);

  if (isPublic) {
    return <img src={src} alt={alt} className={className} onError={onError} />;
  }

  if (!blobUrl) {
    return (
      <div className={`${className} bg-gray-100 flex items-center justify-center`}>
        <span className="text-gray-400 text-xs">…</span>
      </div>
    );
  }

  return <img src={blobUrl} alt={alt} className={className} onError={onError} />;
}
